<template>
  <b-modal
    id="modal-bulk-upload-enrollment"
    ref="modalBulkUpload"
    title="Bulk Enrollment - Upload Excel"
    size="lg"
    @hidden="resetModal"
    @ok="handleUpload">
    <div>
      <!-- Instructions -->
      <div class="alert alert-info mb-4">
        <i class="fe fe-info me-2"></i>
        <strong>Petunjuk:</strong>
        <ul class="mb-0 mt-2">
          <li>Upload file Excel yang sudah diisi sesuai template (.xlsx)</li>
          <li>Ukuran file maksimal 5MB</li>
        </ul>
      </div>

      <!-- File Upload -->
      <div class="form-group">
        <label class="form-label">File Excel <span class="text-danger">*</span></label>
        <div class="custom-file">
          <input 
            type="file" 
            class="custom-file-input" 
            id="excelFileEnrollment"
            accept=".xlsx"
            @change="handleFileChange"
            ref="fileInput">
          <label class="custom-file-label" for="excelFileEnrollment">
            {{ selectedFileName || 'Pilih file Excel...' }}
          </label>
        </div>
        <small class="form-text text-muted">
          Format yang diterima: .xlsx saja (maksimal 5MB)
        </small>
      </div>

      <!-- Selected File Info -->
      <div v-if="selectedFile" class="alert alert-success mt-3">
        <i class="fe fe-file-text me-2"></i>
        <strong>File dipilih:</strong> {{ selectedFile.name }}
        <br>
        <small>Ukuran: {{ formatFileSize(selectedFile.size) }}</small>
      </div>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <button 
        type="button" 
        class="btn btn-secondary" 
        @click="cancel()">
        Batal
      </button>
      <button 
        type="button" 
        class="btn btn-primary"
        :disabled="!selectedFile || isUploading"
        @click="ok()">
        <span v-if="isUploading">
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Mengupload...
        </span>
        <span v-else>
          <i class="fe fe-upload me-2"></i>
          Upload
        </span>
      </button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { buildScopedCompanyMessage, normalizeErrorMessage } from '@/utils/error-message';

export default {
  name: 'ModalBulkUploadEnrollment',
  props: {
    selectedCourseId: {
      type: [Number, String],
      default: null
    },
    idpPeriod: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      selectedFile: null,
      selectedFileName: '',
      isUploading: false
    };
  },
  methods: {
    ...mapActions('mitraEnrollment', ['bulkUploadEnrollment']),
    
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        if (!file.name.endsWith('.xlsx')) {
          this.$swal({
            title: 'Tipe File Tidak Valid',
            text: 'Pilih file Excel dengan format .xlsx',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
          this.resetFile();
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.$swal({
            title: 'File Terlalu Besar',
            text: 'Ukuran file tidak boleh lebih dari 5MB.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
          this.resetFile();
          return;
        }

        this.selectedFile = file;
        this.selectedFileName = file.name;
      }
    },
    
    resetFile() {
      this.selectedFile = null;
      this.selectedFileName = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    
    resetModal() {
      this.resetFile();
      this.isUploading = false;
    },
    
    async handleUpload(bvModalEvent) {
      // Prevent modal from closing
      bvModalEvent.preventDefault();
      
      if (!this.selectedFile) {
        this.$swal({
          title: 'File Belum Dipilih',
          text: 'Pilih file Excel terlebih dahulu',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      if (!this.selectedCourseId) {
        this.$swal({
          title: 'Pelatihan Belum Dipilih',
          text: 'Pilih course terlebih dahulu',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      this.isUploading = true;

      try {
        // Create FormData
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        
        // Add idp_period if available
        if (this.idpPeriod) {
          formData.append('idp_period', this.idpPeriod);
        }

        // Call API
        const result = await this.bulkUploadEnrollment({
          courseId: this.selectedCourseId,
          formData: formData
        });

        // Close upload modal
        this.$refs.modalBulkUpload.hide();

        // Build HTML content for swal
        const htmlContent = this.buildResultHtml(result);

        // Show success message with data mapping
        const errors = result?.data?.errors || result?.data || [];
        this.$swal({
          title: errors.length ? 'Upload Selesai dengan Error' : 'Upload Berhasil',
          html: htmlContent,
          icon: errors.length ? 'warning' : 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: errors.length ? '#f6c343' : '#00c9a7',
          width: errors.length ? '1100px' : '720px'
        });

        // Emit success to refresh data
        this.$emit('upload-success', result);
      } catch (error) {
        const errorMessage = error.code === 'ECONNABORTED'
          ? 'Upload masih diproses terlalu lama dan melewati batas waktu koneksi. Silakan coba upload ulang dengan file yang sama; jika data sudah sebagian masuk, sistem akan menampilkan detail duplikat atau error pada hasil upload berikutnya.'
          : normalizeErrorMessage(error, 'File belum berhasil diupload. Periksa format file lalu coba lagi.');
        
        this.$swal({
          title: 'Upload Gagal',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isUploading = false;
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    buildResultHtml(result) {
      const message = result.message || 'Upload enrollment selesai diproses.';
      const data = result.data || {};
      const errors = Array.isArray(data.errors) ? data.errors : (Array.isArray(data) ? data : []);
      
      let html = `<p style="margin-bottom: 16px;">${message}</p>`;
      
      html += `<div style="text-align: left; background: #f8f9fa; padding: 16px; border-radius: 8px; max-height: 460px; overflow: auto;">`;

      if (errors.length > 0) {
        html += `
          <div style="margin-bottom: 16px;">
            <p style="margin-bottom: 8px; color: #e63757;"><strong><i class="fe fe-x-circle"></i> Detail error (${errors.length} baris)</strong></p>
            <table style="width: 100%; min-width: 980px; font-size: 13px; border-collapse: collapse;">
              <thead>
                <tr style="background: #ffebee;">
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Row</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Sheet</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">NIK</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">TNA ID</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Batch</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Pesan Error</th>
                </tr>
              </thead>
              <tbody>
        `;
        errors.slice(0, 50).forEach((item) => {
          html += `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.row || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.sheet || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.nik || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.tna_id || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.training_event_id || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd; color: #e63757;">${this.escapeHtml(buildScopedCompanyMessage(item))}</td>
            </tr>
          `;
        });
        html += `</tbody></table>`;
        if (errors.length > 50) {
          html += `<p class="mb-0 mt-2 text-muted">Masih ada ${errors.length - 50} error lain. Perbaiki error dari atas dulu lalu upload ulang.</p>`;
        }
        html += `</div>`;
      } else {
        html += `
          <div style="margin-bottom: 16px;">
            <p style="margin-bottom: 0; color: #00a37a;"><strong>Semua data berhasil diproses.</strong></p>
          </div>
        `;
      }

      html += `</div>`;

      return html;
    }
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.custom-file-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
